import React, { Component } from 'react'
import { i18n } from '../i18n/i18n'
import labelVersionApi, { LabelVersionData } from '../apis/labelVersion.api'
import { labelSizeText } from '../apis/labelApproval.api'
import { ConfirmParams } from '../components/Confirm/Confirm.state'
import confirmService from '../services/confirm.service'
import alertService from '../services/alert.service'
import Button from '../components/Button'
import Card from '../components/Card'
import Modal from '../components/Modal'

interface Props {
    isOpen: boolean
    customLabelId: number
    labelName?: string
    onClose: () => void
    onRestored?: () => void
}

interface State {
    versions: LabelVersionData[]
    isLoading: boolean
    restoringId: number | null
    /** Version whose image is open at full size, for comparing fine detail. */
    previewVersion: LabelVersionData | null
}

const formatDate = (value?: string | null) => {
    if (!value) return ''
    return new Date(value).toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
    })
}

/**
 * A label's design history, newest first, with the option to put an earlier
 * design back. Only populated for labels created with version history on.
 */
export default class LabelVersionHistory extends Component<Props, State> {
    state: State = {
        versions: [],
        isLoading: true,
        restoringId: null,
        previewVersion: null
    }

    componentDidMount () {
        this.load()
    }

    load = () => {
        labelVersionApi.list(this.props.customLabelId, versions => {
            this.setState({ versions: versions ?? [], isLoading: false })
        })
    }

    /**
     * Restoring changes what every future print produces, so it is confirmed
     * explicitly rather than done on a single click.
     */
    handleRestoreClick = (version: LabelVersionData) => {
        if (this.state.restoringId) return
        const confirm: ConfirmParams = {
            id: version.id,
            type: 'warning',
            question: `${i18n('Restore version')} ${version.versionNumber}? ${i18n('All future labels and documents will be printed with the selected version.')}`,
            confirmText: i18n('Yes'),
            cancelText: i18n('No'),
            onConfirm: () => this.restore(version)
        }
        confirmService.push(confirm)
    }

    restore = (version: LabelVersionData) => {
        this.setState({ restoringId: version.id })
        labelVersionApi.restore(
            this.props.customLabelId,
            version.id,
            versions => {
                this.setState({ restoringId: null })
                if (!versions) return
                this.setState({ versions })
                alertService.success(i18n('Version restored'))
                if (this.props.onRestored) this.props.onRestored()
            }
        )
    }

    /**
     * "(Current)", "(Approved)" or "(Current - Approved)" beside the version
     * number, so the history says at a glance which designs were signed off.
     */
    statusLabel (version: LabelVersionData) {
        const parts: string[] = []
        if (version.isCurrent) parts.push(i18n('Current'))
        if (version.isApproved) parts.push(i18n('Approved'))
        if (parts.length === 0) return null
        return (
            <span className="ml-2 text-xs text-green-600">
                ({parts.join(' \u2013 ')})
            </span>
        )
    }

    renderVersion = (version: LabelVersionData) => {
        const sizeText = labelSizeText(
            version.labelWidth,
            version.labelHeight,
            version.labelUnit
        )
        return (
            <div
                key={version.id}
                className="flex gap-4 items-start border-b border-solid border-gray-200 py-3 last:border-b-0">
                {version.snapshot
                    ? <button
                        type="button"
                        title={i18n('Click to view larger')}
                        onClick={() => this.setState({ previewVersion: version })}
                        className="w-24 flex-none border border-solid border-gray-200 rounded bg-white p-1 flex items-center justify-center h-16 cursor-pointer hover:border-primary focus:outline-none">
                        <img
                            src={version.snapshot}
                            alt={`v${version.versionNumber}`}
                            style={{ maxWidth: '100%', maxHeight: '100%' }}/>
                      </button>
                    : <div className="w-24 flex-none border border-solid border-gray-200 rounded bg-white p-1 flex items-center justify-center h-16">
                        <span className="text-xs text-secondary">{i18n('No image')}</span>
                      </div>}

                <div className="flex-1 min-w-0">
                    <p className="font-medium">
                        {i18n('Version')} {version.versionNumber}
                        {this.statusLabel(version)}
                    </p>
                    <p className="text-sm text-secondary">
                        {formatDate(version.createdAt)}
                        {version.createdBy ? ` — ${version.createdBy}` : ''}
                    </p>
                    {sizeText && (
                        <p className="text-xs text-secondary">
                            {i18n('Label size')}: {sizeText}
                        </p>
                    )}
                    {version.isApproved && version.approvedBy && (
                        <p className="text-xs text-green-600">
                            {i18n('Approved by')} {version.approvedBy}
                            {version.approvedAt ? ` — ${formatDate(version.approvedAt)}` : ''}
                        </p>
                    )}
                    {version.note && (
                        <p className="text-xs text-secondary">{version.note}</p>
                    )}
                </div>

                <div className="flex-none">
                    {!version.isCurrent && (
                        <Button
                            text={this.state.restoringId === version.id
                                ? i18n('Restoring...')
                                : i18n('Restore')}
                            type="secondary"
                            progress={this.state.restoringId === version.id}
                            disabled={Boolean(this.state.restoringId)}
                            onClick={() => this.handleRestoreClick(version)}/>
                    )}
                </div>
            </div>
        )
    }

    /**
     * Full-size view of one version's image.
     *
     * Rendered here rather than through the shared ImagePopup because that
     * component's overlay has no z-index and would sit behind this modal. The
     * image is shown as large as the viewport allows so small differences —
     * a misspelled code, a shifted barcode — are actually readable.
     */
    renderPreview () {
        const version = this.state.previewVersion
        if (!version || !version.snapshot) return null
        const sizeText = labelSizeText(
            version.labelWidth,
            version.labelHeight,
            version.labelUnit
        )
        const close = () => this.setState({ previewVersion: null })
        return (
            <div
                className="fixed inset-0 flex items-center justify-center p-4"
                style={{ zIndex: 60, background: 'rgba(0,0,0,0.75)' }}
                onClick={close}>
                <div
                    className="bg-white p-4 flex flex-col"
                    style={{ maxWidth: '92vw', maxHeight: '92vh' }}
                    onClick={event => event.stopPropagation()}>
                    <div className="flex items-start justify-between gap-6 mb-3">
                        <div>
                            <p className="font-medium">
                                {i18n('Version')} {version.versionNumber}
                                {this.statusLabel(version)}
                            </p>
                            <p className="text-sm text-secondary">
                                {formatDate(version.createdAt)}
                                {version.createdBy ? ` — ${version.createdBy}` : ''}
                                {sizeText ? ` — ${i18n('Label size')}: ${sizeText}` : ''}
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={close}
                            className="text-gray-400 hover:text-gray-700 text-xl leading-none">
                            ✕
                        </button>
                    </div>
                    <div className="border border-solid border-gray-200 rounded bg-white flex items-center justify-center overflow-auto">
                        <img
                            src={version.snapshot}
                            alt={`${i18n('Version')} ${version.versionNumber}`}
                            style={{ maxWidth: '100%', maxHeight: '75vh' }}/>
                    </div>
                </div>
            </div>
        )
    }

    render () {
        const { versions, isLoading } = this.state
        return (
            <Modal
                title={i18n('Version history')}
                isOpen={this.props.isOpen}
                onClose={this.props.onClose}
                closeDisabled={Boolean(this.state.restoringId)}
                className="max-w-3xl">
                <Card>
                    {this.props.labelName && (
                        <p className="text-sm text-secondary mb-3">
                            {i18n('Label')}: <strong>{this.props.labelName}</strong>
                        </p>
                    )}

                    {isLoading && (
                        <p className="text-secondary">{i18n('Loading...')}</p>
                    )}

                    {!isLoading && versions.length === 0 && (
                        <p className="text-secondary">
                            {i18n('No earlier versions have been saved for this label yet.')}
                        </p>
                    )}

                    {versions.map(this.renderVersion)}
                </Card>
                {this.renderPreview()}
            </Modal>
        )
    }
}
